import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarX2 } from 'lucide-react';
import { Form, FormField, FormMessage } from '@components/ui/form';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { Textarea } from '@components/ui/form/textarea';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { SchedulerShell } from '@components/scheduling/shell';
import { ActionSheet } from '@components/scheduling/actionSheet';
import { useToast } from '@hooks/useToast';
import { cn } from '@utils/cn';
import { useExternalScheduleActions } from '../actions/useActions';
import { TapTip } from './tapTip';
import { ExternalDoneSheet } from './doneSheet';
import { ExternalScheduleMobileLayoutProps } from './types';

const textareaClass =
  'min-h-11 resize-y rounded-md border-[1.5px] px-3 py-2.5 text-base leading-snug';

export const ExternalScheduleMobileLayout = ({
  open,
  setOpen,
  title,
  subtitle,
  form,
  onSubmit,
  contactRequired,
  material,
  completeAfterSave,
  dateBar,
  when,
  banners,
  children,
  withDetails,
  detailsDisabled,
  confirmedTip,
  actions,
  nested,
}: ExternalScheduleMobileLayoutProps) => {
  const { t } = useTranslation('app');
  const { toast } = useToast();
  const [removeOpen, setRemoveOpen] = useState(false);
  const [doneValues, setDoneValues] = useState<Record<string, string>>();
  const {
    toggleDone,
    removeSchedule,
    canMarkDone,
    markDoneDisabled,
    canRemove,
    removeDisabled,
    saveDisabled,
    newSchedule,
  } = useExternalScheduleActions(actions);
  const { completed, readOnly } = actions;

  const submit = async (markDone = false) => {
    if (completeAfterSave) completeAfterSave.current = markDone;
    const { date, time } = form.getValues();
    const missing: string[] = [];
    if (!date) {
      form.setError('date', { message: 'date.required' });
      missing.push(t('forms.date.label'));
    }
    if (!time) {
      form.setError('time', { message: 'time.required' });
      missing.push(t('forms.time.label'));
    }
    if (contactRequired && !(await form.trigger('externalContactId')))
      missing.push(t('forms.name.label'));
    if (missing.length) {
      if (completeAfterSave) completeAfterSave.current = false;
      toast({ title: t('scheduling.external.missing', { fields: missing.join(', ') }) });
      return;
    }
    onSubmit();
  };

  const doneOptions = (material?.options || []).filter((option) => option.id);
  const withDoneSheet = Boolean(completeAfterSave) && !completed && doneOptions.length > 0;
  const doneSheetDisabled = actions.locked || newSchedule;
  const getDoneValues = () =>
    Object.fromEntries(
      doneOptions.map((option) => {
        const current = form.getValues('materialOptions')?.find((el) => el.id === option.id);
        return [
          option.id || '',
          current?.finalQuantity || current?.estimatedQuantity || option.finalQuantity || '',
        ];
      }),
    );

  const confirmDone = (values: Record<string, string>) => {
    const materialOptions = form.getValues('materialOptions') || [];
    materialOptions.forEach((el, index) => {
      if (!(el.id in values)) return;
      form.setValue(`materialOptions.${index}.finalQuantity`, values[el.id], {
        shouldDirty: true,
      });
    });
    setDoneValues(undefined);
    void submit(true);
  };

  const badge = completed
    ? { label: t('actions.done'), className: 'bg-success-100 text-success-800' }
    : newSchedule
      ? {
          label: t('scheduling.external.unscheduled'),
          className: 'bg-neutral-100 text-neutral-600',
        }
      : undefined;

  return (
    <Form {...form}>
      <SchedulerShell
        open={open}
        onOpenChange={setOpen}
        title={
          <span className="flex items-center gap-2">
            <span className="min-w-0 truncate">{title}</span>
            {badge && (
              <span
                className={cn(
                  'flex h-[21px] shrink-0 items-center rounded-full px-2 text-[11px] font-bold',
                  badge.className,
                )}>
                {badge.label}
              </span>
            )}
          </span>
        }
        subtitle={subtitle}
        onBack={nested ? () => setOpen(false) : undefined}
        toolbar={dateBar}
        footer={
          !readOnly && (
            <div className="grid grid-cols-2 gap-2 [&_button]:h-11 [&_button]:w-full [&_button]:justify-center">
              {canRemove && !nested ? (
                <Button
                  variant="dangerSecondary"
                  disabled={removeDisabled}
                  onClick={() => setRemoveOpen(true)}>
                  <CalendarX2 className="h-4 w-4" />
                  {t('actions.remove')}
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  {t('actions.cancel')}
                </Button>
              )}
              <Button
                disabled={saveDisabled}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={() => submit()}>
                {t('actions.save')}
              </Button>
            </div>
          )
        }>
        {banners}
        <div className="-mb-4 flex grow flex-col gap-4 bg-white px-4 pb-8 pt-4 [&_.h-8]:h-11">
          {when}
          <div className="flex flex-col gap-4 [&>div]:min-w-0">{children}</div>
          {withDetails && (
            <>
              <FormField
                control={form.control}
                name="extraComment"
                render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5 text-sm text-neutral-600">
                      {t('forms.extraComment.label')}
                      <TapTip label={t('forms.extraComment.label')}>
                        {t('forms.extraComment.tooltip')}
                      </TapTip>
                    </span>
                    <Textarea
                      {...field}
                      rows={1}
                      disabled={detailsDisabled}
                      className={textareaClass}
                    />
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-neutral-600">{t('forms.note.label')}</span>
                    <Textarea
                      {...field}
                      rows={1}
                      disabled={detailsDisabled}
                      className={textareaClass}
                    />
                  </label>
                )}
              />
              {!readOnly && (
                <FormField
                  control={form.control}
                  name="confirmed"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <div className="flex flex-wrap items-center gap-x-1 gap-y-1.5">
                      <Checkbox
                        {...rest}
                        disabled={detailsDisabled}
                        checked={value}
                        onCheckedChange={onChange}>
                        {t('forms.confirmed.label')}
                      </Checkbox>
                      <TapTip label={t('forms.confirmed.label')}>{confirmedTip}</TapTip>
                    </div>
                  )}
                />
              )}
            </>
          )}
          {canMarkDone && (
            <Button
              variant="secondary"
              disabled={withDoneSheet ? doneSheetDisabled : markDoneDisabled}
              className="h-11 w-full justify-center"
              onClick={withDoneSheet ? () => setDoneValues(getDoneValues()) : toggleDone}>
              <SvgConsumer id="markAsDone" className="text-xl" />
              {completed ? t('scheduling.external.undoDone') : t('actions.markAsDone')}
            </Button>
          )}
          <FormField name="root" render={() => <FormMessage />} />
        </div>
      </SchedulerShell>
      <ExternalDoneSheet
        open={Boolean(doneValues)}
        onDismiss={() => setDoneValues(undefined)}
        options={doneOptions}
        initialValues={doneValues || {}}
        disabled={actions.locked}
        onConfirm={confirmDone}
      />
      <ActionSheet
        open={removeOpen}
        onDismiss={() => setRemoveOpen(false)}
        title={t('scheduling.external.removeTitle')}
        description={t('scheduling.external.removeInfo')}
        actions={
          <div>
            <Button variant="secondary" onClick={() => setRemoveOpen(false)}>
              {t('scheduling.external.keep')}
            </Button>
            <Button
              variant="danger"
              disabled={removeSchedule.isLoading}
              onClick={() => {
                setRemoveOpen(false);
                removeSchedule.mutate();
              }}>
              {t('actions.remove')}
            </Button>
          </div>
        }
      />
    </Form>
  );
};
